import { ref } from "vue";
import { defineStore } from "pinia";
import api from "../api/axios";
import { useProfileStore } from "./profile";

export interface Product {
  id: number;
  name: string;
  categoryIds: string[];
  sellingPrice: number;
  unit: string;
  unit2?: string;
  unit2Factor?: number;
  active: boolean;
  sale: boolean;
  newArrival: boolean;
  packMode: boolean;
  favoriteOrderIndex: number;
}

export interface Category {
  id: string;
  name: string;
  active: boolean;
  orderIndex: number;
  showEmpty: boolean;
  sortFn: (a: Product, b: Product) => number;
}

export const useProductStore = defineStore("products", () => {
  const products = ref<Product[]>([]);
  const categories = ref<Category[]>([]);
  const profileStore = useProfileStore();

  const getProductsByCategoryId = (categoryId: string) => {
    const category = getCategoryById(categoryId);
    if (!category) {
      console.warn(`Category with ID ${categoryId} not found.`);
      return [];
    }
    return products.value
      .filter((product) => product.categoryIds.includes(categoryId))
      .sort(category.sortFn);
  };

  const getCategories = () => {
    return categories.value.sort((a, b) => a.orderIndex - b.orderIndex);
  };

  const getCategoryById = (categoryId: string) => {
    return getCategories().find((category) => category.id === categoryId);
  };

  const getProductById = (productId: number) => {
    return products.value.find((product) => product.id === productId);
  };

  async function initialize() {
    const defaultSortFn = (a: Product, b: Product) => a.name.localeCompare(b.name);
    try {
      const responseCategories = await api.get(
        `/custom/gastrofresh/org/${profileStore.selectedOrganizationId}/categories`,
      );
      console.log("responseCategories", responseCategories.data);
      categories.value = responseCategories.data.rows
        .filter((category: Category) => category.active)
        .map((category: Category) => ({
          ...category,
          // convert category IDs to strings since we add some virtual categories then with "special" IDs
          id: String(category.id),
          showEmpty: false,
          sortFn: defaultSortFn,
        }));
      // Special virtual "all products" category
      categories.value.push({
        id: "all",
        name: "Kompletní sortiment",
        active: true,
        orderIndex: -1000000,
        showEmpty: false,
        sortFn: defaultSortFn,
      });
      categories.value.push({
        id: "favorite",
        name: "Oblíbené",
        active: true,
        orderIndex: -2000000,
        showEmpty: true,
        // first sort by favoriteOrderIndex, then (when favoriteOrderIndex is the same) by name
        sortFn: (a: Product, b: Product) => {
          if (a.favoriteOrderIndex !== b.favoriteOrderIndex) {
            return a.favoriteOrderIndex - b.favoriteOrderIndex;
          }
          //return defaultSortFn(a, b);
          return 0;
        },
      });
      categories.value.push({
        id: "sale",
        name: "Akce a novinky",
        active: false,
        orderIndex: -2000000,
        showEmpty: true,
        // first sort by sale (sale products first), then by name
        sortFn: (a: Product, b: Product) => {
          if (a.sale !== b.sale) {
            return a.sale ? -1 : 1;
          }
          return defaultSortFn(a, b);
        },
      });
      const responseProducts = await api.get(
        `/custom/gastrofresh/org/${profileStore.selectedOrganizationId}/products`,
      );
      console.log("responseProducts", responseProducts.data);
      products.value = responseProducts.data.rows
        .filter((product: Product) => product.active)
        .map((product: Product) => ({
          ...product,
          // convert categoryIds to strings since we add some virtual categories then with "special" IDs
          categoryIds: product.categoryIds.map((id: string) => String(id)),
          // default 'non-favorite' favoriteOrderIndex = "infinity" so that it is always at the end of the list (or left out entirely when preferred)
          favoriteOrderIndex: Number.POSITIVE_INFINITY,
        }));
      // add all products having at least one category to the "all products" category and "favorite" category
      for (const product of products.value) {
        if (product.categoryIds.length > 0 && !product.categoryIds.includes("all")) {
          product.categoryIds.push("all");
          product.categoryIds.push("favorite"); // actually all products will be in the "favorite" category, but sorted by favoriteOrderIndex
        }
      }
      // add "sale" and "newArrival" products to the "sale" category
      for (const product of products.value) {
        if (
          (product.sale && !product.categoryIds.includes("sale")) ||
          (product.newArrival && !product.categoryIds.includes("sale"))
        ) {
          product.categoryIds.push("sale");
        }
      }

      const responseFavorites = await api.get(
        `/custom/gastrofresh/org/${profileStore.selectedOrganizationId}/favorites`,
      );
      console.log("responseFavorites", responseFavorites.data);
      // set favoriteOrderIndex on favorite products corresponding to the order in the favorites list
      responseFavorites.data.rows.forEach((productId: number, index: number) => {
        const product = products.value.find((p) => p.id === productId);
        if (product) {
          product.favoriteOrderIndex = index;
          // add favorite products to the "favorite" category
          if (!product.categoryIds.includes("favorite")) {
            product.categoryIds.push("favorite");
          }
        } else {
          console.log(
            `Favorite product with ID ${productId} not found in products list, ignoring.`,
          );
        }
      });
      // leave out non-favorite products from the "favorite" category (this is now the preferred way)
      for (const product of products.value) {
        if (product.favoriteOrderIndex === Number.POSITIVE_INFINITY) {
          product.categoryIds = product.categoryIds.filter((id) => id !== "favorite");
        }
      }
    } catch (error) {
      console.error("Error initializing products:", error);
      throw error;
    }
  }

  return {
    products,
    initialize,
    getProductsByCategoryId,
    getCategories,
    getCategoryById,
    getProductById,
  };
});
